/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import { useComposerMode } from "cms/components/hooks/use-composer-mode";
import { useFieldZone } from "cms/fields/hooks/use-field-zone";
import { useCurrentDynamicField } from "cms/fields/hooks/use-current-dynamic-field";
import { useSelectFieldSchema } from "cms/fields/hooks/use-select-field-schema";
import { ListItemContext } from "cms/fields/field-lists/field-list-context";
import { ItemRightHandler } from "cms/fields/field-lists/item-right-handler";
import { buildInitialState } from "cms/fields/field-utils";
import { getDefaultFieldImpl } from "cms/fields/field-utils";
import { getDefaultFieldImplPopulated } from "cms/fields/field-utils";
import { getTitle } from "cms/fields/field-utils";
import { AddItemButton } from "cms/fields/layout/add-button/add-item-button";
import debounce from "lodash.debounce";
import { Fragment, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { isArrayNotEmpty } from "utils/empty-exists";
import { FieldListEditDebug } from "./field-list-wrapper-debug";

const FieldListEditWrapper = (props) => {

    const { t } = useTranslation();
    const { editMode, contentOnly } = useComposerMode();
    const { FieldZoneState, applyFieldChanges, setFieldDisabled } = useFieldZone();
    const { field } = useCurrentDynamicField();
    
    const children = props?.children;

    const disabled = FieldZoneState?.disabled ?? false;

    const values = field?.values ?? [];
    const list = field?.list;
    const name = field?.name;
    const type = field?.type;
    const fieldSettings = field?.settings;
    
    const hide = fieldSettings?.hide;
    const options = fieldSettings?.options;
    const applyUnifiedSettings = fieldSettings?.applyUnifiedSettings;
    
    const maxValues = list ? 100 : 1;
    
    const canAddValues = (values && values?.length < maxValues) ?? false;
    
    const schema = useSelectFieldSchema(name);
    const schemaSettings = schema?.settings;
    const defaultValue = schemaSettings.defaultValue ?? null;
    const defaultStyle = schemaSettings?.defaultStyle ?? null;
    const defaultFormat = schemaSettings?.defaultFormat ?? null;
    const noOverride = schemaSettings?.noOverride ?? null;
    const defaultImpl = getDefaultFieldImplPopulated(field?.type, schema);

    // TODO: in a future version - if applyUnifiedSettings then all editors share a single set of styles
    // if (applyUnifiedSettings) {}

    const initialField = { ...field };
    if (values.length === 0) {
        const replacementValues = [];
        replacementValues.push(defaultImpl);
        initialField.values = replacementValues;
    }
    const initialState = buildInitialState(initialField);

    const [localState, setLocalState] = useState(initialState);

    useEffect(() => {
        if (noOverride) {
            setFieldDisabled(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [noOverride]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const update = useCallback(
        debounce((updatedState) => applyFieldChanges(field, updatedState),400),
        [applyFieldChanges]
    );

    const addListItem = async () => {
        const clonedValues = values ? [...values] : [];
        const defaultImpl = structuredClone(getDefaultFieldImpl(type));
        clonedValues.push(defaultImpl);
        const updatedState = { ...localState, values: clonedValues };
        setLocalState(updatedState);
        update(updatedState);
    };
    
    const updateListItem = (changeValue, index) => {
        const clonedValues = values ? [...values] : [];
        clonedValues[index] = changeValue;
        const updatedState = { ...localState, values: clonedValues };
        setLocalState(updatedState);
        update(updatedState);
    };
    
    const removeListItem = async (value, index) => {
        if (isArrayNotEmpty(values)) {
            const updatedValues = [...values];
            updatedValues.splice(index, 1);
            const updatedState = { ...localState, values: updatedValues };
            setLocalState(updatedState);
            update(updatedState);
        }
    };
    
    const changePositions = (params) => {
        const { position, target_position } = params;
        const positioned_components = [...values];
        const [moving_section] = positioned_components.splice(position, 1);
        positioned_components.splice(target_position, 0, moving_section);
        const updatedState = { ...localState, values: positioned_components };
        setLocalState(updatedState);
        update(updatedState);
    };

    return (
        <Fragment>
            <div className='field-editor'>

                {list && canAddValues &&
                    <div style={{ flexBasis: 1, paddingTop: "2px", paddingBottom: "8px" }}>
                        <AddItemButton
                            buttonText={`${t("commons.add")} ${getTitle(field?.type)}`}
                            addListItem={addListItem}
                        />
                    </div>
                }

                {isArrayNotEmpty(localState?.values) &&
                    localState?.values.map((item, index) => {
                        const value = item?.value;
                        const settings = item?.settings;
                        const style = settings?.style;
                        const format = settings?.format;
                        const isDefaultValue = defaultValue === value;
                        const isDefaultStyle = defaultStyle === style;
                        const isDefaultFormat = defaultFormat === format;
                        const wasAlreadyDefault = isDefaultValue && isDefaultStyle && isDefaultFormat;
                        const isMultiple = values.length > 1;
                        const key = `${type}${name}${value}${index}`;
                        // const disabled = wasAlreadyDefault || noOverride || (wasAlreadyDefault && noOverride)
                        // const disabled = noOverride || wasAlreadyDefault
                        const listItemContext = {
                            original: item,
                            index,
                            name,
                            type,
                            value,
                            settings,
                            disabled,
                            wasAlreadyDefault,
                            applyUnifiedSettings,
                            unifiedSettings: { style, format, options },
                            defaultImpl,
                            hide,
                            list,
                            updateListItem,
                            removeListItem,
                            changePositions,
                        };
                        
                        return (
                            <ListItemContext.Provider key={key} value={listItemContext}>
                                <div className='simplicity-composer-field-editor'>
                                    <div className='simplicity-composer-editor-primary'>

                                        {children}

                                    </div>


                                    <div className='simplicity-composer-editor-right'>
                                        {editMode && !contentOnly && isMultiple &&
                                            <ItemRightHandler
                                                currentIndex={index}
                                                value={value}
                                            />
                                        }
                                    </div>
                                </div>

                                <FieldListEditDebug
                                    field={field}
                                    value={value}
                                    settings={settings}
                                />
                                
                                <div className='simplicity-composer-field-editor-divider'/>

                            </ListItemContext.Provider>
                        );
                    })
                }
            </div>
        </Fragment>
    );
};

export { FieldListEditWrapper };
